import { TranslationType } from 'generated/graphql';
import { useABAPQueryStore } from 'stores/CreateABAPQueryStore';
import { useCorporateContentQueryStore } from 'stores/CreateCorporateContentQueryStore';
import { useDitaQueryStore } from 'stores/CreateDitaQueryStore';
import { useMarketingQueryStore } from 'stores/CreateMarketingQueryStore';
import { useNonAbapQueryStore } from 'stores/CreateNonAbapQueryStore';
import { useNotesQueryStore } from 'stores/CreateNotesQueryStore';
import { useTrainingQueryStore } from 'stores/CreateTrainingQueryStore';
import { NotSelectedTranslationType } from 'stores/interfaces/CommonQueryState';
import { validateString } from 'utils/validator';
import {
  PasteToAbapQueryHelper,
  PasteToDitaQueryHelper
} from '../helpers/pasteFromClipboard/translationTools';
import { PasteToNonAbapQueryHelper } from '../helpers/pasteFromClipboard/translationTools/pasteToNonAbapQueryHelper';

export interface CreatePasteFromClipboard {
  enabled: boolean;
  pasteFromClipboard: (text: string) => void;
}

export type PasteFromClipboardHook = () => CreatePasteFromClipboard;

export const usePasteFromClipboardTraining = (): CreatePasteFromClipboard => {
  useTrainingQueryStore(() => undefined);
  return {
    enabled: false,
    pasteFromClipboard: /* istanbul ignore next */ () => {
      throw new Error('Not implemented');
    }
  };
};

export const usePasteFromClipboardNotSelected = (): CreatePasteFromClipboard => {
  useTrainingQueryStore(() => undefined);
  return {
    enabled: false,
    pasteFromClipboard: /* istanbul ignore next */ () => {
      throw new Error('Choose a translation type first.');
    }
  };
};

export const usePasteFromClipboardCorporateContent = (): CreatePasteFromClipboard => {
  useCorporateContentQueryStore(() => undefined);
  return {
    enabled: false,
    pasteFromClipboard: /* istanbul ignore next */ () => {
      throw new Error('Not implemented');
    }
  };
};

export const usePasteFromClipboardNotes = (): CreatePasteFromClipboard => {
  useNotesQueryStore(() => undefined);
  return {
    enabled: false,
    pasteFromClipboard: /* istanbul ignore next */ () => {
      throw new Error('Not implemented');
    }
  };
};

export const usePasteFromClipboardMarketing = (): CreatePasteFromClipboard => {
  useMarketingQueryStore(() => undefined);
  return {
    enabled: false,
    pasteFromClipboard: /* istanbul ignore next */ () => {
      throw new Error('Not implemented');
    }
  };
};

export const usePasteFromClipboardDita = (): CreatePasteFromClipboard => {
  const store = useDitaQueryStore();
  const helper = new PasteToDitaQueryHelper();

  const pasteFromClipboard = (text: string) => {
    const {
      fileName,
      fileTitle,
      loio,
      deliverable,
      cmsProject,
      sourceContact,
      submap,
      languagesTranslatorTeams
    } = helper.getDataFromText(text);

    store.setFileName(fileName);
    store.setFileTitle(fileTitle);
    store.setLoio(loio);
    store.setDeliverable(deliverable);
    store.setCmsProject(cmsProject);
    store.setSourceContact(sourceContact);
    store.setSubmap(submap);
    store.setLanguagesTranslatorTeams(languagesTranslatorTeams);

    validateString(fileName, store.setFileNameValueState);
    validateString(fileTitle, store.setFileTitleValueState);
    validateString(loio, store.setLoioValueState);
    validateString(deliverable, store.setDeliverableValueState);
    validateString(cmsProject, store.setCmsProjectValueState);
    validateString(sourceContact, store.setSourceContactValueState);
  };

  return { enabled: true, pasteFromClipboard };
};

export const usePasteFromClipboardNonAbap = (): CreatePasteFromClipboard => {
  const store = useNonAbapQueryStore();
  const helper = new PasteToNonAbapQueryHelper();
  const pasteFromClipboard = (text: string) => {
    const {
      xtmProjectName,
      repository,
      collection,
      fileName,
      sourceContact,
      languagesTranslatorTeams
    } = helper.getDataFromText(text);

    store.setXtmProjectName(xtmProjectName);
    store.setRepository(repository);
    store.setCollection(collection);
    store.setFileName(fileName);
    store.setSourceContact(sourceContact);
    store.setLanguagesTranslatorTeams(languagesTranslatorTeams);

    validateString(xtmProjectName, store.setXtmProjectNameValueState);
    validateString(repository, store.setRepositoryValueState);
    validateString(collection, store.setCollectionValueState);
    validateString(fileName, store.setFileNameValueState);
    validateString(sourceContact, store.setSourceContactValueState);
  };

  return { enabled: true, pasteFromClipboard };
};

export const usePasteFromClipboardAbap = (): CreatePasteFromClipboard => {
  const store = useABAPQueryStore();
  const helper = new PasteToAbapQueryHelper();

  const pasteFromClipboard = (text: string) => {
    const {
      system,
      objectType,
      objectName,
      collectionPackage,
      collectionOwner,
      applicationComponent,
      repositoryProgramId,
      translationArea,
      languagesTranslatorTeams,
      repositoryProgramType,
      repositoryProgramName,
      objectOwner,
      maxLength
    } = helper.getDataFromText(text);

    store.setSystem(system);
    store.setObjectType(objectType);
    store.setObjectName(objectName);
    store.setObjectOwner(objectOwner);
    store.setCollectionPackage(collectionPackage);
    store.setCollectionOwner(collectionOwner);
    store.setLanguagesTranslatorTeams(languagesTranslatorTeams);
    store.setApplicationComponent(applicationComponent);
    store.setRepositoryProgramType(repositoryProgramType);
    store.setRepositoryProgramId(repositoryProgramId);
    store.setRepositoryProgramName(repositoryProgramName);
    store.setTranslationArea(translationArea);
    store.setMaxLength(maxLength != '' ? Number(maxLength) : null);

    validateString(system, store.setSystemValueState);
    validateString(objectType, store.setObjectTypeValueState);
    validateString(objectName, store.setObjectNameValueState);
    validateString(collectionPackage, store.setCollectionPackageValueState);
    validateString(applicationComponent, store.setApplicationComponentValueState);
    validateString(repositoryProgramType, store.setRepositoryProgramTypeValueState);
    validateString(repositoryProgramId, store.setRepositoryProgramIdValueState);
    validateString(repositoryProgramName, store.setRepositoryProgramNameValueState);
    validateString(translationArea, store.setTranslationAreaValueState);
  };

  return { enabled: true, pasteFromClipboard };
};

export const getClipBoardHook = (
  type: TranslationType | NotSelectedTranslationType
): PasteFromClipboardHook => {
  switch (type) {
    case TranslationType.DITA:
      return usePasteFromClipboardDita;
    case TranslationType.NON_ABAP:
      return usePasteFromClipboardNonAbap;
    case TranslationType.TRAINING:
      return usePasteFromClipboardTraining;
    case TranslationType.CORPORATE_CONTENT:
      return usePasteFromClipboardCorporateContent;
    case TranslationType.ABAP:
      return usePasteFromClipboardAbap;
    case TranslationType.NOTES:
      return usePasteFromClipboardNotes;
    case TranslationType.MARKETING:
      return usePasteFromClipboardMarketing;
    case NotSelectedTranslationType.NOT_SELECTED_TRANSLATION_TYPE:
    default:
      return usePasteFromClipboardNotSelected;
  }
};
